import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Layout as AntLayout,
  Spin,
  Result,
  /*Breadcrumb*/
} from 'antd';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';

import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';

import {
  updateUser,
  logout,
  updateActiveMenuKey,
} from '../../store/reducers/loginSlice';
import SiderMenu from '../../components/Layout/SiderMenu';
import HeaderMenu from '../../components/Layout/HeaderMenu';
import { createAbility } from '../../utils/ability';
import { ME_QUERY } from '../../apollo/queries';
import AccountSettings from '../Login/AccountSettings';

import './layout.css';

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: normal;
  margin-left: 1rem;
`;

const Logo = styled.div`
  font-family: Righteous;
  font-size: 1.75rem;
  color: #fff;
  text-align: center;
  height: 64px;
  line-height: 64px;
  ${(props) =>
    props.collapsed &&
    `
      width: 40px;
      overflow: hidden;
      margin: auto;
    `}
`;

const LogoIcon = styled.span`
  border: 2px solid #c4c4c4;
  padding: 0 5px;
  margin-right: 0.25rem;
`;

const { Header, Footer, Sider, Content } = AntLayout;
// const { SubMenu } = Menu;

const Layout = ({ title, children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [collapsed, setCollapsible] = useState(true);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const { user, activeMenukey } = useSelector((state) => state.login);

  const { loading, data, error } = useQuery(ME_QUERY);

  const onLogoutClick = () => {
    localStorage.removeItem('token');
    dispatch(logout());
    history.push('/');
  };

  const onCollapse = (collapsed) => {
    setCollapsible(collapsed);
  };

  const onMenuClick = (path) => {
    dispatch(updateActiveMenuKey({ menuKey: `path:${path}` }));
    history.push(path);
  };

  const onAccountSettingsClick = () => {
    setShowAccountSettings(true);
  };

  useEffect(() => {
    if (data) {
      if (data.me) {
        const ability = createAbility(data.me);
        dispatch(updateUser({ user: { ...data.me, ability } }));

        //update active menu
        dispatch(updateActiveMenuKey({ menuKey: `path:${location.pathname}` }));
      } else if (data.me === null) {
        //assume token expired
        localStorage.removeItem('token');
        dispatch(logout());
        history.push('/');
      }
    }
  }, [data, history, dispatch, location.pathname]);

  return (
    <Spin spinning={loading}>
      <AntLayout style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={onCollapse}
          trigger={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            zIndex: 2,
            backgroundColor: process.env.REACT_APP_ENV === 'dev' ? 'gold' : '',
          }}
        >
          <Logo collapsed={collapsed}>
            <LogoIcon>M</LogoIcon>
            MirDocs
          </Logo>
          <SiderMenu
            activeKeys={activeMenukey}
            onMenuClick={onMenuClick}
            ability={user && user.ability ? user.ability : {}}
          />
        </Sider>

        <AntLayout className="site-layout" style={{}}>
          <Header
            className="site-layout-background"
            style={{
              padding: 0,
              position: 'fixed',
              zIndex: 1,
              width: '100%',
              paddingLeft: `${collapsed ? '80px' : '200px'}`,
            }}
          >
            <Title>
              {title}
              {/* <Breadcrumb style={{ margin: '16px 0' }}>
              <Breadcrumb.Item>Home</Breadcrumb.Item>
              <Breadcrumb.Item>Employees</Breadcrumb.Item>
            </Breadcrumb> */}
            </Title>
            {data && data.me && (
              <HeaderMenu
                user={data.me}
                onLogoutClick={onLogoutClick}
                onAccountSettingsClick={onAccountSettingsClick}
              />
            )}
          </Header>

          {showAccountSettings && (
            <AccountSettings
              onSuccess={() => setShowAccountSettings(false)}
              onCancelClick={() => setShowAccountSettings(false)}
            />
          )}

          {user && (
            <Content
              style={{
                margin: '5rem 2rem 1rem 1rem',
                paddingLeft: `${collapsed ? '80px' : '200px'}`,
              }}
            >
              {children}
            </Content>
          )}
          {error && (
            <Result
              status="500"
              title="500"
              subTitle="Sorry, something went wrong."
              style={{ marginTop: '5rem' }}
            />
          )}
          <Footer style={{ textAlign: 'center' }}>MirDocs ©2021</Footer>
        </AntLayout>
      </AntLayout>
    </Spin>
  );
};

Layout.defaultProps = {
  title: '',
};

Layout.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.any,
};

export default Layout;
